// TF4-F3 / PO9 (docs/build-briefs/TF4-F3-abc2go-export-in-bottles.md).
//
// The one place that converts between a purchase line's ORDER UNIT (a case,
// a bottle, a keg) and the base units (bottles) it contains. Pack size
// ("how many of the base unit make up one order unit", migration 0076) is the
// only multiplier. Pure and exact: scaled BigInt arithmetic through
// inventoryQuantity.ts, never a JS float, so 0.1 case of 3 is refused rather
// than rounded to a plausible bottle count.

import { formatQuantity, multiplyScaledQuantities, parseQuantity } from "./inventoryQuantity.ts";
import { halfUpDivide } from "./pricing.ts";

const SCALE = 10_000n;

// UX66: an order unit that is itself the base unit being ordered, so a missing
// pack size is 1 by construction. The same vocabulary purchasing.ts reads.
const SINGLE_UNIT_ORDER_UNITS: ReadonlySet<string> = new Set(["unit", "keg", "bottle", "can"]);

export function isSingleUnitOrderUnit(orderUnit: string): boolean {
  return SINGLE_UNIT_ORDER_UNITS.has(orderUnit.trim().toLowerCase());
}

/** Bottles (base units) in one order unit, as a minimal decimal string, or
 * null when it is not known. A pack size as written wins; otherwise a
 * single-unit order unit is 1; otherwise unknown. A zero or negative pack is
 * not a pack. */
export function bottlesPerOrderUnitOf(
  orderUnit: string | null,
  packSize: string | null,
): string | null {
  if (packSize !== null) {
    const scaled = parseQuantity(packSize, "pack size");
    return scaled > 0n ? formatQuantity(scaled) : null;
  }
  if (orderUnit !== null && isSingleUnitOrderUnit(orderUnit)) {
    return "1";
  }
  return null;
}

/** quantity (in order units) x bottlesPerOrderUnit, as a whole number of
 * bottles; null when the pack is unknown or the product is not a whole
 * number of bottles. */
export function bottleCountOf(quantity: string, bottlesPerOrderUnit: string | null): number | null {
  if (bottlesPerOrderUnit === null) {
    return null;
  }
  const product = multiplyScaledQuantities(
    parseQuantity(quantity, "quantity"),
    parseQuantity(bottlesPerOrderUnit, "pack size"),
  );
  if (product < 0n || product % SCALE !== 0n) {
    return null;
  }
  return Number(product / SCALE);
}

/** PO9: whole order units needed to cover a shortfall (in base units),
 * rounded UP, never fewer than one for a positive shortfall, together with
 * the bottles that many order units hold. Null when the pack is unknown, the
 * shortfall is not positive, or the covered bottles are not whole. */
export function orderUnitsCoveringShortfall(
  shortfall: string,
  bottlesPerOrderUnit: string | null,
): { orderQuantity: string; bottlesCovered: number } | null {
  if (bottlesPerOrderUnit === null) {
    return null;
  }
  const shortfallScaled = parseQuantity(shortfall, "shortfall");
  const packScaled = parseQuantity(bottlesPerOrderUnit, "pack size");
  if (shortfallScaled <= 0n || packScaled <= 0n) {
    return null;
  }
  let units = (shortfallScaled + packScaled - 1n) / packScaled;
  if (units < 1n) {
    units = 1n;
  }
  const orderQuantity = units.toString();
  const bottlesCovered = bottleCountOf(orderQuantity, bottlesPerOrderUnit);
  return bottlesCovered === null ? null : { orderQuantity, bottlesCovered };
}

/** PO7-S4-F2: a cost stated per ORDER UNIT (a case price) as whole cents per
 * bottle, rounded half up once. The inverse of bottleCostToOrderUnitCents. */
export function orderUnitCostToBottleCents(
  orderUnitCostCents: number,
  bottlesPerOrderUnit: string,
): number {
  const pack = parseQuantity(bottlesPerOrderUnit, "pack size");
  return Number(halfUpDivide(BigInt(orderUnitCostCents) * SCALE, pack));
}

/** PO7-S4-F2: whole cents per bottle as cents per order unit (for showing a
 * receipt's cost beside an invoice's case price), rounded half up once. */
export function bottleCostToOrderUnitCents(
  bottleCostCents: number,
  bottlesPerOrderUnit: string,
): number {
  const pack = parseQuantity(bottlesPerOrderUnit, "pack size");
  return Number(halfUpDivide(BigInt(bottleCostCents) * pack, SCALE));
}
